/**
 * The 10 visual themes (LevelData.theme). Every theme is a palette plus a procedural
 * backdrop; all of them keep the dark neon look of the app: dark sky, glowing edges, and
 * spikes with a light outline so they read on every background.
 */
import { THEME_COUNT } from '@cube-rush/shared';

/** Procedural backdrop styles (see background.ts). */
export type BackdropKind =
  | 'synthwave'
  | 'ocean'
  | 'city'
  | 'mountains'
  | 'rain'
  | 'volcano'
  | 'clouds'
  | 'crystals'
  | 'forest'
  | 'planets';

export interface Theme {
  /** For debugging and the dev harness. */
  name: string;
  backdrop: BackdropKind;
  /** Sky gradient: top, middle, horizon. */
  sky: readonly [string, string, string];
  /** Backdrop silhouettes, far to near, and the backdrop's light accents (stars, windows). */
  far: string;
  mid: string;
  near: string;
  glow: string;
  /** Ground and ceiling fill, its dark bottom, and the glowing edge line. */
  ground: string;
  groundDark: string;
  line: string;
  /** Blocks: fill, outline and the inner tile pattern. */
  block: string;
  blockLine: string;
  blockInner: string;
  /** Spikes: fill and outline. */
  spike: string;
  spikeLine: string;
  /** Highlights (finish line, confetti). */
  accent: string;
  /**
   * A holiday version of the theme (see seasonTheme.ts): its backdrop gets the season's art.
   * Absent on the themes themselves.
   */
  season?: 'halloween' | 'winter';
}

export const THEMES: readonly Theme[] = [
  {
    name: 'synthwave',
    backdrop: 'synthwave',
    sky: ['#0d0221', '#2b0a4f', '#7a1d6e'],
    far: '#3a0f5c',
    mid: '#24093f',
    near: '#160629',
    glow: '#ffd60a',
    ground: '#1d0940',
    groundDark: '#0b0320',
    line: '#ff4fd8',
    block: '#1e0b45',
    blockLine: '#2de2ff',
    blockInner: '#3b1d7a',
    spike: '#0a0614',
    spikeLine: '#ffffff',
    accent: '#ff4fd8',
  },
  {
    name: 'ocean',
    backdrop: 'ocean',
    sky: ['#020c22', '#05305a', '#0f6a8c'],
    far: '#0a3a62',
    mid: '#06284a',
    near: '#041a33',
    glow: '#d8f6ff',
    ground: '#04213d',
    groundDark: '#010a17',
    line: '#2de2ff',
    block: '#05284a',
    blockLine: '#7ef3ff',
    blockInner: '#0e4a72',
    spike: '#020814',
    spikeLine: '#eafcff',
    accent: '#2de2ff',
  },
  {
    name: 'city',
    backdrop: 'city',
    sky: ['#170430', '#6b1240', '#ff7a3d'],
    far: '#4a0f3c',
    mid: '#2c0828',
    near: '#18041a',
    glow: '#ffd27a',
    ground: '#26081f',
    groundDark: '#0e020b',
    line: '#ff9a1f',
    block: '#2c0a2e',
    blockLine: '#ffd60a',
    blockInner: '#4f1844',
    spike: '#120414',
    spikeLine: '#fff4cf',
    accent: '#ff9a1f',
  },
  {
    name: 'mountains',
    backdrop: 'mountains',
    sky: ['#050822', '#142a6b', '#4a6fd0'],
    far: '#2a3f8a',
    mid: '#14214f',
    near: '#0a1336',
    glow: '#e6f0ff',
    ground: '#0b1640',
    groundDark: '#030719',
    line: '#8dd3ff',
    block: '#0f1d52',
    blockLine: '#b8e2ff',
    blockInner: '#213a88',
    spike: '#050816',
    spikeLine: '#ffffff',
    accent: '#6fb7ff',
  },
  {
    name: 'rain',
    backdrop: 'rain',
    sky: ['#010805', '#022a15', '#07502a'],
    far: '#0a5a2e',
    mid: '#05361b',
    near: '#03200f',
    glow: '#7dff9e',
    ground: '#031a0c',
    groundDark: '#000903',
    line: '#22e07a',
    block: '#04220f',
    blockLine: '#8dff2a',
    blockInner: '#0d4220',
    spike: '#010603',
    spikeLine: '#dcffe3',
    accent: '#22e07a',
  },
  {
    name: 'volcano',
    backdrop: 'volcano',
    sky: ['#160200', '#4a0a02', '#b02d0c'],
    far: '#5c1406',
    mid: '#350b03',
    near: '#1e0602',
    glow: '#ffb238',
    ground: '#2a0802',
    groundDark: '#100200',
    line: '#ff9a1f',
    block: '#2e0a04',
    blockLine: '#ffb238',
    blockInner: '#521709',
    spike: '#100302',
    spikeLine: '#ffeccc',
    accent: '#ff5a1f',
  },
  {
    name: 'candy',
    backdrop: 'clouds',
    sky: ['#26093a', '#7a1f7a', '#ff7fd6'],
    far: '#b0489e',
    mid: '#8a3086',
    near: '#5a1a62',
    glow: '#fff0fb',
    ground: '#3b0e49',
    groundDark: '#17041e',
    line: '#ffd1f5',
    block: '#481462',
    blockLine: '#ff9ae6',
    blockInner: '#6c2588',
    spike: '#1a0520',
    spikeLine: '#ffffff',
    accent: '#ff4fd8',
  },
  {
    name: 'ice',
    backdrop: 'crystals',
    sky: ['#031424', '#0b3550', '#3b92b8'],
    far: '#2a6f93',
    mid: '#174c6e',
    near: '#0c2f4a',
    glow: '#e8fdff',
    ground: '#082640',
    groundDark: '#020c17',
    line: '#c4f4ff',
    block: '#0c3352',
    blockLine: '#e0fbff',
    blockInner: '#1a5680',
    spike: '#04121f',
    spikeLine: '#ffffff',
    accent: '#9ff0ff',
  },
  {
    name: 'forest',
    backdrop: 'forest',
    sky: ['#03130c', '#0b3a26', '#24774a'],
    far: '#145a35',
    mid: '#0c3c23',
    near: '#062214',
    glow: '#eaff7a',
    ground: '#072414',
    groundDark: '#010a05',
    line: '#b6ff4a',
    block: '#0b2d1a',
    blockLine: '#d4ff7a',
    blockInner: '#16492b',
    spike: '#020a05',
    spikeLine: '#f4ffe0',
    accent: '#b6ff4a',
  },
  {
    name: 'space',
    backdrop: 'planets',
    sky: ['#010003', '#0d0622', '#251346'],
    far: '#3d2470',
    mid: '#2a1852',
    near: '#170c30',
    glow: '#fff4c2',
    ground: '#160b28',
    groundDark: '#06020d',
    line: '#ffd60a',
    block: '#1b1033',
    blockLine: '#ffd60a',
    blockInner: '#33205c',
    spike: '#08040f',
    spikeLine: '#fff7d1',
    accent: '#ffd60a',
  },
];

if (THEMES.length !== THEME_COUNT) {
  throw new Error(`THEMES has ${THEMES.length} entries, THEME_COUNT is ${THEME_COUNT}`);
}

/** The theme for a level's theme index; odd values (negative, fractional, huge) still work. */
export function themeFor(index: number): Theme {
  const i = Number.isFinite(index) ? Math.floor(Math.abs(index)) % THEMES.length : 0;
  return THEMES[i]!;
}
